import { Injectable } from '@angular/core';
import { OktaAuthStateService } from '@okta/okta-angular';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Provides role-checking observables derived from the Okta auth state.
 *
 * Use `isAdmin$` for the common admin-role check, or `hasRole$()` for
 * any arbitrary role string defined in the environment or elsewhere.
 *
 * Prefer subscribing via `takeUntilDestroyed` in components to keep
 * cleanup automatic, consistent with the rest of the application.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthRoleService {
  /** Emits true when the current user belongs to the configured admin role. */
  readonly isAdmin$: Observable<boolean>;

  constructor(private readonly oktaState: OktaAuthStateService) {
    this.isAdmin$ = this.oktaState
      .hasAnyGroups(environment.adminRole)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  /**
   * Returns an observable that emits true when the current user belongs
   * to the given role group string.
   */
  hasRole$(role: string): Observable<boolean> {
    return this.oktaState.hasAnyGroups(role);
  }
}
